import type { VocabCardFields } from "@/types/VocabCard";
import { Button, Card, Modal } from "@ai-teach/ui-web";
import type React from "react";
import { useCallback, useRef, useState } from "react";
import { AiFillPicture } from "react-icons/ai";
import { HiVolumeOff, HiVolumeUp } from "react-icons/hi";
import { RiDeleteBin6Fill } from "react-icons/ri";

interface VocabCardProps {
  vocabCard: VocabCardFields;
  onEdit: (vocabCard: VocabCardFields) => void;
  onDelete: (id: string) => void;
}

const PART_OF_SPEECH_ABBREV: Record<string, string> = {
  noun: "n",
  verb: "v",
  adjective: "adj",
  adverb: "adv",
  pronoun: "pron",
  preposition: "prep",
  conjunction: "conj",
  interjection: "interj",
  article: "art",
};

function highlightWord(sentence: string, word: string): React.ReactNode {
  if (!sentence.includes(word)) return sentence;

  const regex = new RegExp(`(${word})`, "gi");
  const parts = sentence.split(regex);

  return parts.map((part, index) =>
    part.toLowerCase() === word.toLowerCase() ? (
      <span key={`${part}-${index + 1}`} className="text-primary-500 font-bold">
        {part}
      </span>
    ) : (
      part
    )
  );
}

export function VocabCardComponent({ vocabCard, onEdit, onDelete }: VocabCardProps) {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  const partOfSpeechAbbrev = vocabCard.partOfSpeech
    ? PART_OF_SPEECH_ABBREV[vocabCard.partOfSpeech] || vocabCard.partOfSpeech
    : null;

  const handlePlayAudio = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation(); // Prevent card click from triggering edit
      if (!vocabCard.audioUrl) return;

      if (audioRef.current) {
        if (isPlaying) {
          audioRef.current.pause();
          audioRef.current.currentTime = 0;
          setIsPlaying(false);
        } else {
          audioRef.current.play();
          setIsPlaying(true);
        }
      }
    },
    [vocabCard.audioUrl, isPlaying]
  );

  const handleAudioEnded = useCallback(() => {
    setIsPlaying(false);
  }, []);

  const handleDeleteClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsDeleteModalOpen(true);
  };

  const handleConfirmDelete = () => {
    onDelete(vocabCard.id);
    setIsDeleteModalOpen(false);
  };

  return (
    <>
      <Card
        onClick={() => onEdit(vocabCard)}
        hoverable={true}
        className="group relative shadow-lg p-5 flex flex-col gap-4"
      >
        {/* Delete Button - visible on hover */}
        <button
          type="button"
          onClick={handleDeleteClick}
          className="absolute top-3 right-3 p-1.5 rounded-full bg-red-100 dark:bg-red-900/30 text-red-500 opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-200 dark:hover:bg-red-900/50 cursor-pointer"
          aria-label="Delete vocabulary card"
        >
          <RiDeleteBin6Fill className="w-4 h-4" />
        </button>

        {/* Hidden Audio Element */}
        {vocabCard.audioUrl && (
          <audio ref={audioRef} src={vocabCard.audioUrl} onEnded={handleAudioEnded} preload="none">
            <track kind="captions" label={vocabCard.word} />
          </audio>
        )}

        {/* Header: Speaker + Word + Part of Speech */}
        <div className="flex items-center gap-2 overflow-hidden">
          {/* Speaker Icon */}
          <button
            type="button"
            onClick={handlePlayAudio}
            disabled={!vocabCard.audioUrl}
            className={`p-1.5 rounded-full transition-colors ${
              vocabCard.audioUrl
                ? "hover:bg-primary-100 dark:hover:bg-primary-900/30 cursor-pointer"
                : "opacity-50 cursor-not-allowed"
            } ${isPlaying ? "bg-primary-100 dark:bg-primary-900/30" : ""}`}
            aria-label={isPlaying ? "Stop pronunciation" : "Play pronunciation"}
          >
            {isPlaying ? (
              <HiVolumeOff className="w-6 h-6 text-primary-500" />
            ) : (
              <HiVolumeUp className="w-6 h-6 text-primary-500" />
            )}
          </button>

          {/* Word */}
          <span className="text-xl font-bold truncate">{vocabCard.word}</span>

          {/* Part of Speech */}
          {partOfSpeechAbbrev && <span className="text-sm text-muted">({partOfSpeechAbbrev})</span>}
        </div>

        {/* Definition */}
        <p className="text-secondary-200 overflow-hidden">"{vocabCard.definition}"</p>

        {/* Image */}
        <div
          className={`aspect-[4/3] rounded-xl overflow-hidden flex items-center justify-center
        ${vocabCard.imageUrl ? "bg-surface" : "bg-gray-200 dark:bg-gray-800"}`}
        >
          {vocabCard.imageUrl ? (
            <img
              src={vocabCard.imageUrl}
              alt={vocabCard.word}
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="text-gray-400 dark:text-gray-500">
              <AiFillPicture className="w-12 h-12" />
            </div>
          )}
        </div>

        {/* Example Sentence */}
        <p className="leading-relaxed overflow-hidden">
          {highlightWord(vocabCard.example, vocabCard.word)}
        </p>
      </Card>

      {isDeleteModalOpen && (
        <Modal
          isOpen={isDeleteModalOpen}
          onClose={() => setIsDeleteModalOpen(false)}
          title="Warning!"
          size="sm"
          style="danger"
        >
          <div className="flex flex-col">
            <p className="text-secondary-200 mb-6 text-center">
              Are you sure you want to delete "{vocabCard.word}"? This action cannot be undone.
            </p>
            <div className="flex gap-3 justify-between">
              <Button onClick={() => setIsDeleteModalOpen(false)} variant="secondary">
                Cancel
              </Button>
              <Button onClick={handleConfirmDelete} variant="danger">
                Delete
              </Button>
            </div>
          </div>
        </Modal>
      )}
    </>
  );
}
